import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { esTardio } from 'lib/tardio';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ClipboardList, Link2, FileDown, Loader2, ImageIcon, ArrowRight, ArrowLeft, SearchX, Check, PencilLine, Ban, ChevronRight,
  Spline, TreeDeciduous,
} from 'lucide-react';
import { fetchInspeccionDetalle, anularInspeccion, signedUrls, fetchNombres } from 'services/adminService';
import { fetchArbolesPorIds } from 'services/arbolesService';
import { fetchLineasPorIds } from 'services/cablesService';
import { arbolesDeInspeccion, rutaArbol, nombreArbol } from 'lib/arboles';
import { lineasDeInspeccion, rutaLinea, tramo } from 'lib/cables';
import {
  fechaCorta, fechaHoraCorta, nombreUsuario, idsUsuarios, metodoIdentificacion, METODO_LABEL,
} from 'lib/auditoria';
import {
  huellaInspeccion, rutaInspeccion, origenTexto, nombreArchivoInspeccion,
} from 'lib/auditoriaInspeccion';
import { checklistDe, instalacionDe, etiquetaInstalacion } from 'lib/auditoriaInstalaciones';
import { ESTADO_FILA } from 'lib/estadoUi';
import { useAuth } from 'contexts/AuthContext';
import AppShell from 'components/AppShell';
import Cargando from 'components/Cargando';
import useNombres from 'components/useNombres';
import EstadoPill, { MarcaTardio, MarcaAnulada } from 'components/EstadoPill';
import MotivoDialog from 'components/MotivoDialog';
import Button from 'components/ui/Button';
import ErrorState from 'components/ui/ErrorState';
import EmptyState from 'components/ui/EmptyState';
import { mensajeError } from 'lib/errores';

// jspdf solo baja al generar el PDF.
const cargarPdf = () => import('lib/inspeccionPdf');

// Ruta del asistente en modo corrección (lo implementa /inspeccion).
export const rutaCorreccion = (id) => `/inspeccion?corrige=${encodeURIComponent(id)}`;

// Usuario de Auth por nombre (cables_nombres) o UUID corto; "usted" si es quien mira.
const quien = (uuid, user, nombres) => {
  if (!uuid) return 'Sin dato';
  const n = nombreUsuario(uuid, nombres);
  return uuid === user?.id ? `${n} (usted)` : n;
};

function Foto({ ruta, url, alt, clase }) {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" title="Abrir la foto en tamaño completo">
        <img src={url} alt={alt} loading="lazy" className={`${clase} w-full rounded-xl object-cover ring-1 ring-black/5`} />
      </a>
    );
  }
  return (
    <div className={`${clase} flex flex-col items-center justify-center gap-1 rounded-xl bg-muted text-center text-[11px] text-muted-foreground`}>
      {url === null ? <><ImageIcon size={14} aria-hidden="true" />Foto no disponible</> : <Loader2 size={14} className="animate-spin" aria-label="Cargando foto" />}
      <span className="sr-only">{ruta}</span>
    </div>
  );
}

function Dato({ k, children }) {
  return (
    <>
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="min-w-0 text-foreground">{children}</dd>
    </>
  );
}

export default function InspeccionDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, user } = useAuth();
  const esAdmin = role === 'admin';

  const [estado, setEstado] = useState({ cargando: true, error: null, inspeccion: null, corregidaPor: [] });
  const [urls, setUrls] = useState({});
  const [arboles, setArboles] = useState({ ids: [], filas: [], error: false });
  const [cables, setCables] = useState({ ids: [], filas: [], error: false });
  const [aviso, setAviso] = useState('');
  const [enlaceManual, setEnlaceManual] = useState('');
  const [generando, setGenerando] = useState(false);
  const [errorPdf, setErrorPdf] = useState('');
  const [anulando, setAnulando] = useState(false);

  const cargar = useCallback(async () => {
    setEstado({ cargando: true, error: null, inspeccion: null, corregidaPor: [] });
    setUrls({});
    try {
      const d = await fetchInspeccionDetalle(id);
      setEstado({ cargando: false, error: null, ...d });
    } catch (e) {
      setEstado({ cargando: false, error: e, inspeccion: null, corregidaPor: [] });
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const r = estado.inspeccion;
  const nombres = useNombres(useMemo(() => (r ? idsUsuarios([r]) : []), [r]));

  // Árboles que la inspección marcó en el ítem fitosanitario (alerta o falla).
  useEffect(() => {
    const ids = r ? arbolesDeInspeccion(r) : [];
    setArboles({ ids, filas: [], error: false });
    if (!ids.length) return undefined;
    let vivo = true;
    fetchArbolesPorIds(ids).then((filas) => {
      if (vivo) setArboles({ ids, filas: filas || [], error: filas === null });
    });
    return () => { vivo = false; };
  }, [r]);

  // Cables que marcó en "Estado del cable" o "Punto de anclaje".
  useEffect(() => {
    const ids = r ? lineasDeInspeccion(r) : [];
    setCables({ ids, filas: [], error: false });
    if (!ids.length) return undefined;
    let vivo = true;
    fetchLineasPorIds(ids).then((filas) => {
      if (vivo) setCables({ ids, filas: filas || [], error: filas === null });
    });
    return () => { vivo = false; };
  }, [r]);

  useEffect(() => {
    if (!r) return undefined;
    const rutas = [r.firma_url, ...(r.fotos || []), ...Object.values(r.detalles || {}).flatMap((d) => d?.fotos || [])];
    let vivo = true;
    signedUrls(rutas).then((m) => { if (vivo) setUrls(m); });
    return () => { vivo = false; };
  }, [r?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const enlace = `${window.location.origin}${rutaInspeccion(id)}`;
  const copiarEnlace = async () => {
    setEnlaceManual('');
    try {
      await navigator.clipboard.writeText(enlace);
      setAviso('Enlace copiado');
      setTimeout(() => setAviso(''), 2500);
    } catch {
      setEnlaceManual(enlace); // sin portapapeles: se muestra para copiarlo a mano
    }
  };

  const pdf = async () => {
    setGenerando(true);
    setErrorPdf('');
    try {
      const sha256 = await huellaInspeccion(r);
      const generadoPor = (user?.id && (await fetchNombres([user.id]))[user.id]) || user?.email;
      const { generarPdfInspeccion } = await cargarPdf();
      const doc = await generarPdfInspeccion({
        inspeccion: r, corregidaPor: estado.corregidaPor, generadoPor,
        generadoAt: new Date().toISOString(), sha256, nombres,
      });
      doc.save(nombreArchivoInspeccion(r, 'pdf'));
    } catch (e) {
      setErrorPdf(mensajeError(e, { contexto: 'No se pudo generar el PDF.', porDefecto: 'Intente de nuevo.' }));
    } finally {
      setGenerando(false);
    }
  };

  const confirmarAnulacion = async (motivo) => {
    const fila = await anularInspeccion(r.id, motivo);
    setEstado((s) => ({ ...s, inspeccion: { ...s.inspeccion, ...fila } }));
  };

  let cuerpo;
  if (estado.cargando) {
    cuerpo = <Cargando texto="Cargando la inspección…" />;
  } else if (estado.error) {
    cuerpo = <ErrorState title="No se pudo cargar la inspección" message={mensajeError(estado.error)} onRetry={cargar} />;
  } else if (!r) {
    cuerpo = (
      <EmptyState framed icon={<SearchX size={24} />} title="No se encontró la inspección"
        description="El enlace no corresponde a una inspección que su usuario pueda ver. Revise que esté completo."
        action={<Button asChild variant="outline" size="sm"><Link to="/inspecciones"><ClipboardList size={16} className="mr-1.5" aria-hidden="true" />Ir a Inspecciones</Link></Button>} />
    );
  } else {
    const det = r.detalles || {};
    const inst = instalacionDe(r);
    const secciones = checklistDe(inst)?.secciones || [];
    const metodo = metodoIdentificacion(r, nombres);
    cuerpo = (
      <div className="space-y-4">
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={`text-lg font-bold text-foreground ${r.anulada ? 'line-through' : ''}`}>{fechaCorta(r.fecha)}</h2>
            <EstadoPill estado={r.estado_general} />
            {esTardio(r) && <MarcaTardio />}
            {r.anulada && <MarcaAnulada />}
          </div>

          {r.anulada && (
            <div role="note" data-testid="aviso-anulada" className="mt-3 rounded-xl border border-error/40 bg-error/5 p-3 text-sm text-foreground">
              <span className="font-semibold text-error">Inspección anulada.</span> Motivo: {r.anulada_motivo || 'sin motivo'}
              <span className="block text-xs text-muted-foreground">
                Anulada el {fechaHoraCorta(r.anulada_at) || 'sin dato'} (hora del servidor) por {quien(r.anulada_por, user, nombres)}.
              </span>
              <span className="block text-xs text-muted-foreground">No cuenta en indicadores ni reportes.</span>
            </div>
          )}

          {(r.corrige_a || estado.corregidaPor.length > 0) && (
            <div className="mt-3 space-y-1.5 rounded-xl border border-border bg-muted/40 p-3 text-sm">
              {r.corrige_a && (
                <p className="flex flex-wrap items-center gap-1.5">
                  <ArrowLeft size={14} className="text-muted-foreground" aria-hidden="true" />
                  Esta inspección es una corrección.
                  <Link to={rutaInspeccion(r.corrige_a)} className="font-medium text-primary underline-offset-2 hover:underline">
                    Ver la inspección que corrige
                  </Link>
                </p>
              )}
              {estado.corregidaPor.length > 0 && (
                <div>
                  <p className="flex items-center gap-1.5">
                    <ArrowRight size={14} className="text-muted-foreground" aria-hidden="true" />
                    Corregida por:
                  </p>
                  <ul className="ml-5 mt-0.5 list-disc">
                    {estado.corregidaPor.map((c) => (
                      <li key={c.id}>
                        <Link to={rutaInspeccion(c.id)} className="font-medium text-primary underline-offset-2 hover:underline">
                          {fechaCorta(c.fecha)} · {c.guia_nombre || 'Sin nombre'}{c.created_at ? ` · registrada ${fechaHoraCorta(c.created_at)}` : ''}
                        </Link>
                        {c.anulada && <span className="text-xs text-muted-foreground"> (anulada)</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
            <Dato k="Instalación">{etiquetaInstalacion(inst)}</Dato>
            <Dato k="Inspector">
              <span>{r.guia_nombre || 'Sin nombre'}</span>
              {r.pin_sesion_id && (
                <span className="block text-xs text-muted-foreground">Ingresó con PIN: el nombre lo fijó la base según la sesión de PIN.</span>
              )}
            </Dato>
            <Dato k="Identificación">{METODO_LABEL[metodo]}</Dato>
            {r.iniciada_at && <Dato k="Hora de inicio"><span className="tabular-nums">{fechaHoraCorta(r.iniciada_at)}</span> <span className="text-xs text-muted-foreground">(dispositivo)</span></Dato>}
            <Dato k="Hora del dispositivo"><span className="tabular-nums">{fechaHoraCorta(r.fecha_hora) || 'Sin dato'}</span></Dato>
            <Dato k="Hora del servidor"><span className="tabular-nums">{fechaHoraCorta(r.created_at) || 'Sin dato'}</span></Dato>
            <Dato k="Origen">{origenTexto(r.origen)}</Dato>
            <Dato k="Registro">
              {esTardio(r)
                ? <span className="text-warning-foreground">Tardío (registrado después del día de la inspección)</span>
                : (r.registro_tardio ? 'Declarada (registrada después del día de la inspección)' : 'Del mismo día')}
            </Dato>
            <Dato k="Registrada por">
              <span>{quien(r.created_by, user, nombres)}</span>
              {r.created_by && <span className="block break-all font-mono text-[11px] text-muted-foreground">{r.created_by}</span>}
            </Dato>
            {r.colaborador_id && <Dato k="Colaborador ID"><span className="break-all font-mono text-xs">{r.colaborador_id}</span></Dato>}
            <Dato k="ID"><span className="break-all font-mono text-xs">{r.id}</span></Dato>
          </dl>
        </section>

        {secciones.map((s) => (
          <section key={s.id || s.titulo} aria-labelledby={`sec-${s.id}`} className="rounded-xl border border-border bg-card p-4">
            <h2 id={`sec-${s.id}`} className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">{s.titulo}</h2>
            <ul className="space-y-1.5">
              {(s.items || []).map((it) => {
                const est = r.resultados?.[it.key];
                const d = det[it.key] || {};
                const fotos = Array.isArray(d.fotos) ? d.fotos : [];
                return (
                  <li key={it.key} data-testid={`item-${it.key}`} className={`rounded-xl px-3 py-2.5 ${ESTADO_FILA[est] || ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground">{it.label}</span>
                      <EstadoPill estado={est} />
                    </div>
                    {d.nota && <p className="mt-1.5 whitespace-pre-line text-sm leading-snug text-foreground">{d.nota}</p>}
                    {fotos.length > 0 && (
                      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {fotos.map((p, i) => (
                          <Foto key={p} ruta={p} url={urls[p]} alt={`${it.label}, foto ${i + 1}`} clase="h-36" />
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-1 text-sm font-bold uppercase tracking-wide text-muted-foreground">Comentarios</h2>
          {r.comentarios
            ? <p className="whitespace-pre-line text-sm leading-snug text-foreground">{r.comentarios}</p>
            : <p className="text-sm italic text-muted-foreground">Sin comentarios.</p>}
        </section>

        {(r.fotos || []).length > 0 && (
          <section className="rounded-xl border border-border bg-card p-4">
            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">Fotos generales</h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {r.fotos.map((p, i) => <Foto key={p} ruta={p} url={urls[p]} alt={`Foto general ${i + 1}`} clase="h-36" />)}
            </div>
          </section>
        )}

        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">Firma</h2>
          {!r.firma_url ? (
            <p className="text-sm italic text-muted-foreground">Sin firma registrada.</p>
          ) : urls[r.firma_url] ? (
            <img src={urls[r.firma_url]} alt="Firma del inspector" className="h-28 rounded-xl border border-border bg-white" />
          ) : (
            <p className="text-sm text-muted-foreground">{r.firma_url in urls ? 'Firma no disponible.' : 'Cargando la firma…'}</p>
          )}
        </section>

        {cables.ids.length > 0 && (
          <section aria-labelledby="t-cables" data-testid="cables-inspeccion" className="rounded-xl border border-border bg-card p-4">
            <h2 id="t-cables" className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">Cables señalados</h2>
            {cables.error ? (
              <p className="text-sm text-muted-foreground">No se pudieron cargar los cables señalados en esta inspección.</p>
            ) : (
              <ul className="divide-y divide-border">
                {cables.ids.map((cid) => {
                  const c = cables.filas.find((x) => x.id === cid);
                  const t = c ? tramo(c) : '';
                  return (
                    <li key={cid}>
                      <Link to={rutaLinea(cid)} className="flex items-center gap-2 py-2 text-sm hover:bg-muted/40">
                        <Spline size={16} className="shrink-0 text-primary" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                          <span className="font-semibold text-foreground">{c ? [c.codigo, c.nombre].filter(Boolean).join(' · ') : 'Cable'}</span>
                          {t && <span className="text-muted-foreground"> · {t}</span>}
                        </span>
                        <ChevronRight size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {arboles.ids.length > 0 && (
          <section aria-labelledby="t-arboles" data-testid="arboles-inspeccion" className="rounded-xl border border-border bg-card p-4">
            <h2 id="t-arboles" className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">Árboles señalados</h2>
            {arboles.error ? (
              <p className="text-sm text-muted-foreground">No se pudieron cargar los árboles señalados en esta inspección.</p>
            ) : (
              <ul className="divide-y divide-border">
                {arboles.ids.map((aid) => {
                  const a = arboles.filas.find((x) => x.id === aid);
                  return (
                    <li key={aid}>
                      <Link to={rutaArbol(aid)} className="flex items-center gap-2 py-2 text-sm hover:bg-muted/40">
                        <TreeDeciduous size={16} className="shrink-0 text-primary" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                          <span className="font-semibold text-foreground">{a ? nombreArbol(a) : 'Árbol'}</span>
                          {a?.sostiene && <span className="text-muted-foreground"> · sostiene {a.sostiene}</span>}
                        </span>
                        <ChevronRight size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>
    );
  }

  const acciones = r ? (
    <>
      <Button type="button" variant="outline" onClick={copiarEnlace}>
        {aviso ? <Check size={16} className="mr-1.5 text-success" aria-hidden="true" /> : <Link2 size={16} className="mr-1.5" aria-hidden="true" />}
        Copiar enlace
      </Button>
      <Button type="button" variant="outline" onClick={pdf} loading={generando}>
        {!generando && <FileDown size={16} className="mr-1.5" aria-hidden="true" />}
        {generando ? 'Generando…' : 'Descargar PDF'}
      </Button>
      {esAdmin && !r.anulada && (
        <>
          <Button type="button" variant="outline" onClick={() => navigate(rutaCorreccion(r.id))}
            title="Registra una inspección nueva que corrige esta; la original queda anulada con el motivo">
            <PencilLine size={16} className="mr-1.5" aria-hidden="true" /> Registrar corrección
          </Button>
          <Button type="button" variant="outline" onClick={() => setAnulando(true)}
            className="border-error/40 text-error hover:bg-error hover:text-error-foreground">
            <Ban size={16} className="mr-1.5" aria-hidden="true" /> Anular
          </Button>
        </>
      )}
      {aviso && <span role="status" className="text-sm text-success">{aviso}</span>}
    </>
  ) : null;

  return (
    <AppShell titulo="Inspección" descripcion={r ? `${etiquetaInstalacion(instalacionDe(r))} · ${r.guia_nombre || 'Sin nombre'}` : undefined}
      acciones={acciones}>
      {enlaceManual && (
        <label className="mb-3 block text-xs text-muted-foreground">
          No se pudo copiar automáticamente. Copie el enlace:
          <input readOnly value={enlaceManual} onFocus={(e) => e.target.select()}
            className="mt-1 h-9 w-full rounded-lg border border-input bg-white px-2.5 font-mono text-xs text-foreground" />
        </label>
      )}
      {errorPdf && <p role="alert" className="mb-3 rounded-lg bg-error/10 px-3 py-2 text-sm text-error">{errorPdf}</p>}

      {cuerpo}

      <MotivoDialog open={anulando} onClose={() => setAnulando(false)} peligro
        titulo="Anular inspección" confirmar="Anular"
        etiqueta="Motivo de la anulación"
        descripcion={r && (
          <>La inspección del {fechaCorta(r.fecha)} de {r.guia_nombre || 'sin nombre'} quedará anulada: se sigue viendo,
            con su motivo, pero no cuenta en indicadores ni reportes. No se puede deshacer. Si lo que hay que hacer es
            corregir datos, use &quot;Registrar corrección&quot;.</>
        )}
        onConfirm={confirmarAnulacion} />
    </AppShell>
  );
}
